import styled from '@emotion/styled';
import { Popover } from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import { getRoute } from '@shared/hooks/getRoute';
import EditProfileContainer from '@shared/hooks/state/EditProfileContainer';
import ProfilesContainer from '@shared/hooks/state/ProfilesContainer';
import IContentDialogProps from '@shared/models/components/IContentDialogProps';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import ContentDialog from '../core/dialog/ContentDialog';

export interface ProfileOptionsPopoverProps {
    /** Disables all options that modify the profile (isPreview, !canManage, etc.) */
    disabled?: boolean;
}

/**
 * Self-contained "Options" button + popover shared between ProfileHeader
 * (desktop) and OrgProfileEditPage (admin). Pass `showCopy` / `showShare`
 * to enable the options that only apply to user-owned profiles.
 */
export function ProfileOptionsPopover({ disabled = false }: ProfileOptionsPopoverProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { organizationId } = useParams<{ organizationId?: string }>();
    const editProfileContainer = EditProfileContainer.useContainer();
    const profilesContainer = ProfilesContainer.useContainer();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'profile-options-popover' : undefined;
    const [dialogConfig, setDialogConfig] = useState<IContentDialogProps | undefined>();

    const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleDelete = async () => {
        if (!editProfileContainer.id) return;
        const result = await profilesContainer.deleteProfile(editProfileContainer.id);
        if (result?.resultType == 'Ok') {
            navigate(-1);
        }
    };
    const copyProfile = () => {
        showDialog(undefined);

        if (!editProfileContainer.originalProfileRef.current) return;

        // Clone the profile with updated name
        const updatedName = t(`common:profile.copy.profileSuffix`, `${editProfileContainer.name} (copy)`, {
            name: editProfileContainer.name,
        });
        const clonedProfile = editProfileContainer.getClonedProfile(
            editProfileContainer.originalProfileRef.current,
            editProfileContainer.id,
            updatedName
        );
        editProfileContainer.updateProfileState(clonedProfile);
        editProfileContainer.setHasChanges(true);

        // Navigate to appropriate new profile route based on context
        const newProfilePath = organizationId ? `/org/${organizationId}/profiles/new` : '/profiles/create';
        navigate(newProfilePath, { replace: true });
    };

    const showDialog = (config: IContentDialogProps | undefined) => {
        setDialogConfig(config);
    };

    const closeDialog = () => {
        setDialogConfig(undefined);
    };

    const handleShowDialog = (config: string) => {
        handleClose(); // Close the popover first
        if (config == 'delete') {
            showDialog(deleteConfig);
        } else if (config == 'copy') {
            showDialog(copyConfig);
        }
    };
    const deleteConfig: IContentDialogProps = {
        open: true,
        title: t('common:profile.delete.titleLong'),
        description: t('common:profile.delete.confirmLong'),
        cancelButtonText: t('common:cancel'),
        confirmButtonText: t('common:profile.delete.confirmButton'),
        onConfirm: handleDelete,
        onClose: closeDialog,
        onCancel: closeDialog,
    };
    const copyConfig: IContentDialogProps = {
        open: true,
        title: t('common:profile.copy.question'),
        description: t('common:profile.copy.description'),
        cancelButtonText: t('common:cancel'),
        confirmButtonText: t('common:profile.copy.confirm'),
        onConfirm: copyProfile,
        onClose: closeDialog,
        onCancel: closeDialog,
    };

    return (
        <>
            <Button
                $color="quick"
                endIcon="more"
                $size="small"
                iconSize={20}
                aria-describedby={id}
                onClick={handleOpen}>
                {t('common:options')}
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <PopoverContent>
                    <Button
                        $color="secondary"
                        $size="small"
                        startIcon="automation"
                        href={getRoute(`${location.pathname}/advanced`)}>
                        {t('common:advancedOptions')}
                    </Button>
                    <Button
                        $color="secondary"
                        $size="small"
                        startIcon="layers"
                        disabled={disabled}
                        href={getRoute(`${location.pathname}/advanced/summary`)}>
                        {t('common:profile.summary.title')}
                    </Button>

                    <Button
                        $color="secondary"
                        $size="small"
                        startIcon="duplicate"
                        disabled={disabled}
                        onClick={() => handleShowDialog('copy')}>
                        {t('common:profile.copy.title')}
                    </Button>

                    <Button
                        $color="secondary"
                        $size="small"
                        startIcon="userGroups"
                        disabled={disabled}
                        to={`./advanced/share`}>
                        {t('common:profile.share.title')}
                    </Button>

                    <Button
                        $color="secondary"
                        $size="small"
                        startIcon="delete"
                        disabled={disabled}
                        onClick={() => handleShowDialog('delete')}>
                        {t('common:profile.delete.title')}
                    </Button>
                </PopoverContent>
            </Popover>
            {dialogConfig && <ContentDialog {...dialogConfig} onClose={closeDialog} />}
        </>
    );
}

const PopoverContent = styled.div`
    width: 300px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 1rem;
`;
